import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { characterAPI, gameAPI, sessionAPI } from '../services/api';
import AdventureSetup from '../components/AdventureSetup';
import JoinGameModal from '../components/JoinGameModal';
import LoadingSpinner from '../components/LoadingSpinner';
import './Dashboard.css';

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [characters, setCharacters] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [startingGame, setStartingGame] = useState(null);
  const [setupCharacter, setSetupCharacter] = useState(null); // Character for adventure setup
  const [joinCharacter, setJoinCharacter] = useState(null); // Character for joining multiplayer
  const [deleteConfirm, setDeleteConfirm] = useState(null); // Character to confirm deletion

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [charsRes, gamesRes] = await Promise.all([
        characterAPI.getAll(),
        gameAPI.getAll()
      ]);
      setCharacters(charsRes.data.characters);
      setGames(gamesRes.data.games);
    } catch (err) {
      console.error('Load error:', err);
      setError('Failed to load data. Is the server running?');
    } finally {
      setLoading(false);
    }
  };

  const openAdventureSetup = (character) => {
    setSetupCharacter(character);
  };

  const openJoinGame = (character) => {
    setJoinCharacter(character);
  };

  const handleJoinGame = (game) => {
    // Navigate to the joined game
    navigate(`/play/adventure/${game._id}`);
  };

  const startNewGame = async ({ difficulty, setting }) => {
    if (!setupCharacter) return;

    setStartingGame(setupCharacter._id);
    setError('');
    try {
      const response = await gameAPI.create({
        characterId: setupCharacter._id,
        difficulty,
        setting
      });
      navigate(`/play/adventure/${response.data.game._id}`);
    } catch (err) {
      console.error('Start game error:', err);
      setError(err.response?.data?.error || 'Failed to start new game. Check if MongoDB is running.');
      setStartingGame(null);
      setSetupCharacter(null);
    }
  };

  const continueGame = (gameId) => {
    navigate(`/play/adventure/${gameId}`);
  };

  const deleteGame = async (gameId) => {
    if (!confirm('Are you sure you want to delete this save?')) return;
    try {
      await gameAPI.delete(gameId);
      setGames(games.filter(g => g._id !== gameId));
    } catch (err) {
      setError('Failed to delete game');
    }
  };

  const confirmDeleteCharacter = async () => {
    if (!deleteConfirm) return;
    try {
      await characterAPI.delete(deleteConfirm._id);
      setCharacters(characters.filter(c => c._id !== deleteConfirm._id));
      setGames(games.filter(g => g.character?._id !== deleteConfirm._id));
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete character');
      setDeleteConfirm(null);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const capitalizeFirst = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : '';

  if (loading) {
    return <LoadingSpinner fullScreen message="Loading your realm..." />;
  }

  return (
    <div className="dashboard">
      {/* Loading overlay when starting a new game */}
      {startingGame && (
        <LoadingSpinner fullScreen message="Preparing your adventure..." />
      )}
      <header className="dashboard-header">
        <div className="header-content">
          <h1>TomorrowDungeons</h1>
          <div className="header-actions">
            <span className="welcome-text">Welcome, {user?.username}</span>
            <button onClick={logout} className="btn btn-secondary">
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="dashboard-content">
        {error && <div className="dashboard-error">{error}</div>}

        {/* Characters Section */}
        <section className="dashboard-section">
          <div className="section-header">
            <h2>Your Characters</h2>
            <Link to="/play/create-character" className="btn btn-primary">
              Create Character
            </Link>
          </div>

          {characters.length === 0 ? (
            <div className="empty-state card">
              <p>You haven't created any characters yet.</p>
              <p className="text-muted">Create a character to begin your adventure!</p>
            </div>
          ) : (
            <div className="characters-grid">
              {characters.map(char => (
                <div key={char._id} className="character-card card">
                  <button
                    className="character-delete-btn"
                    onClick={() => setDeleteConfirm(char)}
                    title="Delete character"
                  >
                    <span>&#x1F5D1;</span>
                  </button>
                  {char.appearance?.portraitUrl && (
                    <div className="character-portrait-wrapper">
                      <img
                        src={char.appearance.portraitUrl}
                        alt={`Portrait of ${char.name}`}
                        className="character-card-portrait"
                      />
                    </div>
                  )}
                  <div className="character-info">
                    <h3>{char.name}</h3>
                    <p className="character-details">
                      Level {char.level} {capitalizeFirst(char.species?.primary)} {capitalizeFirst(char.class)}
                    </p>
                  </div>
                  <div className="character-actions">
                    <button
                      onClick={() => openAdventureSetup(char)}
                      className="btn btn-primary"
                      disabled={startingGame !== null}
                    >
                      New Adventure
                    </button>
                    <button
                      onClick={() => openJoinGame(char)}
                      className="btn btn-secondary"
                      disabled={startingGame !== null}
                    >
                      Join Game
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Saved Games Section */}
        <section className="dashboard-section">
          <div className="section-header">
            <h2>Saved Adventures</h2>
          </div>

          {games.length === 0 ? (
            <div className="empty-state card">
              <p>No saved adventures.</p>
              <p className="text-muted">Start a new adventure with one of your characters!</p>
            </div>
          ) : (
            <div className="games-list">
              {games.map(game => (
                <div key={game._id} className="game-card card">
                  <div className="game-info">
                    <h3>{game.title}</h3>
                    <p className="game-character">
                      {game.character?.name} - Level {game.character?.level} {capitalizeFirst(game.character?.class)}
                    </p>
                    <p className="game-location text-muted">
                      {game.currentLocation?.name || 'Unknown Location'}
                    </p>
                    <p className="game-date text-muted">
                      Last played: {formatDate(game.lastActivity)}
                    </p>
                  </div>
                  <div className="game-actions">
                    <button
                      onClick={() => continueGame(game._id)}
                      className="btn btn-primary"
                    >
                      Continue
                    </button>
                    <button
                      onClick={() => deleteGame(game._id)}
                      className="btn btn-danger"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Adventure Setup Modal */}
      {setupCharacter && (
        <AdventureSetup
          character={setupCharacter}
          onStart={startNewGame}
          onCancel={() => setSetupCharacter(null)}
        />
      )}

      {/* Join Game Modal */}
      {joinCharacter && (
        <JoinGameModal
          character={joinCharacter}
          onJoin={handleJoinGame}
          onCancel={() => setJoinCharacter(null)}
        />
      )}

      {/* Delete Character Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-content delete-modal">
            <h3>Delete Character?</h3>
            <p>
              Are you sure you want to delete <strong>{deleteConfirm.name}</strong>?
              All associated saves will be permanently lost.
            </p>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={confirmDeleteCharacter}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
